import React from 'react';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import {
    Box, Button, Divider, FormHelperText, InputLabel,
    MenuItem, OutlinedInput, Select, Typography
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import PageHeader from '../../../components/page-header';
import BrazilianNumberInput from '../../../components/brazilian-number-input';
import { useAmendmentCreateEditController } from './context/amendment-create-edit-controller';
import { useAmendmentCreateEditState } from './context/amendment-create-edit-context';
import { AMENDMENT_TYPES, AMENDMENT_STATUSES } from '../../../model/sge/amendment/amendment-enums';

export function AmendmentCreateEditComponent() {
    const { handleOnChange, handleBudgetUnitChange, handleBudgetProgramChange, handleFilesChange, handleSave, handleCancel, isSaving } = useAmendmentCreateEditController();
    const {
        amendmentRequest, parlamentarians, politicalParties,
        budgetUnits, selectedBudgetUnitId, budgetPrograms, selectedBudgetProgramId, budgetActions,
        showValidationErrors, amendmentId, files
    } = useAmendmentCreateEditState();
    const isEdit = !!amendmentId;

    return (
        <React.Fragment>
            <Grid item xs={12}>
                <PageHeader
                    title={isEdit ? 'Editar Emenda' : 'Nova Emenda Parlamentar'}
                    subtitle={isEdit ? 'Atualize as informações da emenda parlamentar' : 'Cadastre uma nova emenda parlamentar no sistema'}
                    breadcrumbs={[{ label: 'Emendas', href: '/emendas' }, { label: isEdit ? 'Editar' : 'Nova' }]}
                />
            </Grid>

            <Grid item xs={12}>
                <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column' }} elevation={3}>
                    <Grid container spacing={3}>

                        {/* Identificação */}
                        <Grid item xs={12}>
                            <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', fontWeight: 600 }}>
                                Identificação
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                        </Grid>

                        <Grid item xs={12} md={8}>
                            <InputLabel required>Nome</InputLabel>
                            <OutlinedInput
                                fullWidth
                                autoComplete="off"
                                name="name"
                                value={amendmentRequest.name || ''}
                                onChange={handleOnChange}
                                error={showValidationErrors && !amendmentRequest.name}
                            />
                            {showValidationErrors && !amendmentRequest.name && (
                                <FormHelperText error>Nome é obrigatório</FormHelperText>
                            )}
                        </Grid>

                        <Grid item xs={12} md={2}>
                            <InputLabel required>Exercício</InputLabel>
                            <OutlinedInput
                                fullWidth
                                autoComplete="off"
                                name="year"
                                type="number"
                                value={amendmentRequest.year || ''}
                                onChange={handleOnChange}
                                error={showValidationErrors && !amendmentRequest.year}
                            />
                            {showValidationErrors && !amendmentRequest.year && (
                                <FormHelperText error>Exercício é obrigatório</FormHelperText>
                            )}
                        </Grid>

                        <Grid item xs={12} md={2}>
                            <InputLabel required>Status</InputLabel>
                            <Select fullWidth name="status" value={amendmentRequest.status ?? ''} onChange={handleOnChange} error={showValidationErrors && !amendmentRequest.status}>
                                <MenuItem value=""><em>Selecionar</em></MenuItem>
                                {AMENDMENT_STATUSES.map((s) => <MenuItem key={s.id} value={s.id}>{s.id} - {s.label}</MenuItem>)}
                            </Select>
                            {showValidationErrors && !amendmentRequest.status && (
                                <FormHelperText error>Status é obrigatório</FormHelperText>
                            )}
                        </Grid>

                        {/* Vinculação e Valores */}
                        <Grid item xs={12}>
                            <Typography variant="h6" sx={{ mb: 2, mt: 1, color: 'primary.main', fontWeight: 600 }}>
                                Vinculação e Valores
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                        </Grid>

                        <Grid item xs={12} md={3}>
                            <InputLabel required>Tipo</InputLabel>
                            <Select fullWidth name="type" value={amendmentRequest.type ?? ''} onChange={handleOnChange} error={showValidationErrors && !amendmentRequest.type}>
                                <MenuItem value=""><em>Selecionar</em></MenuItem>
                                {AMENDMENT_TYPES.map((t) => <MenuItem key={t.id} value={t.id}>{t.id} - {t.label}</MenuItem>)}
                            </Select>
                            {showValidationErrors && !amendmentRequest.type && (
                                <FormHelperText error>Tipo é obrigatório</FormHelperText>
                            )}
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <InputLabel required>Parlamentar</InputLabel>
                            <Select fullWidth name="parlamentarId" value={amendmentRequest.parlamentarId || ''} onChange={handleOnChange} error={showValidationErrors && !amendmentRequest.parlamentarId}>
                                <MenuItem value=""><em>Selecionar</em></MenuItem>
                                {parlamentarians.map((p) => <MenuItem key={p.id} value={p.id}>{p.personName}</MenuItem>)}
                            </Select>
                            {showValidationErrors && !amendmentRequest.parlamentarId && (
                                <FormHelperText error>Parlamentar é obrigatório</FormHelperText>
                            )}
                        </Grid>

                        <Grid item xs={12} md={3}>
                            <InputLabel required>Partido Político</InputLabel>
                            <Select fullWidth name="politicalPartyId" value={amendmentRequest.politicalPartyId || ''} onChange={handleOnChange} error={showValidationErrors && !amendmentRequest.politicalPartyId}>
                                <MenuItem value=""><em>Selecionar</em></MenuItem>
                                {politicalParties.map((p) => <MenuItem key={p.id} value={p.id}>{p.abbreviation} - {p.name}</MenuItem>)}
                            </Select>
                            {showValidationErrors && !amendmentRequest.politicalPartyId && (
                                <FormHelperText error>Partido Político é obrigatório</FormHelperText>
                            )}
                        </Grid>

                        <Grid item xs={12} md={2}>
                            <InputLabel required>Valor Total</InputLabel>
                            <BrazilianNumberInput
                                fullWidth
                                value={amendmentRequest.totalAmount || 0}
                                onChange={(value) => handleOnChange({ target: { name: 'totalAmount', value } })}
                                decimals={2}
                                prefix="R$"
                                placeholder="0,00"
                                error={showValidationErrors && !amendmentRequest.totalAmount}
                            />
                            {showValidationErrors && !amendmentRequest.totalAmount && (
                                <FormHelperText error>Valor Total é obrigatório</FormHelperText>
                            )}
                        </Grid>

                        {/* Classificação Orçamentária */}
                        <Grid item xs={12}>
                            <Typography variant="h6" sx={{ mb: 2, mt: 1, color: 'primary.main', fontWeight: 600 }}>
                                Classificação Orçamentária
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <InputLabel required>Unidade Orçamentária</InputLabel>
                            <Select fullWidth name="selectedBudgetUnitId" value={selectedBudgetUnitId ?? ''} onChange={handleBudgetUnitChange} error={showValidationErrors && !selectedBudgetUnitId}>
                                <MenuItem value=""><em>Selecionar</em></MenuItem>
                                {budgetUnits.map((u) => <MenuItem key={u.id} value={u.id}>{u.code} - {u.name} ({u.year})</MenuItem>)}
                            </Select>
                            {showValidationErrors && !selectedBudgetUnitId && (
                                <FormHelperText error>Unidade Orçamentária é obrigatória</FormHelperText>
                            )}
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <InputLabel required>Programa Orçamentário</InputLabel>
                            <Select
                                fullWidth
                                name="selectedBudgetProgramId"
                                disabled={!selectedBudgetUnitId}
                                value={selectedBudgetProgramId ?? ''}
                                onChange={handleBudgetProgramChange}
                                error={showValidationErrors && !selectedBudgetProgramId}
                            >
                                <MenuItem value=""><em>Selecionar</em></MenuItem>
                                {budgetPrograms.map((p) => <MenuItem key={p.id} value={p.id}>{p.code} - {p.name}</MenuItem>)}
                            </Select>
                            {!selectedBudgetUnitId ? (
                                <FormHelperText>Selecione a Unidade Orçamentária</FormHelperText>
                            ) : showValidationErrors && !selectedBudgetProgramId && (
                                <FormHelperText error>Programa Orçamentário é obrigatório</FormHelperText>
                            )}
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <InputLabel required>Ação Orçamentária</InputLabel>
                            <Select
                                fullWidth
                                name="budgetActionId"
                                disabled={!selectedBudgetProgramId}
                                value={amendmentRequest.budgetActionId ?? ''}
                                onChange={handleOnChange}
                                error={showValidationErrors && !amendmentRequest.budgetActionId}
                            >
                                <MenuItem value=""><em>Selecionar</em></MenuItem>
                                {budgetActions.map((a) => <MenuItem key={a.id} value={a.id}>{a.code} - {a.name}</MenuItem>)}
                            </Select>
                            {!selectedBudgetProgramId ? (
                                <FormHelperText>Selecione o Programa Orçamentário</FormHelperText>
                            ) : showValidationErrors && !amendmentRequest.budgetActionId && (
                                <FormHelperText error>Ação Orçamentária é obrigatória</FormHelperText>
                            )}
                        </Grid>

                        {/* Informações Adicionais */}
                        <Grid item xs={12}>
                            <Typography variant="h6" sx={{ mb: 2, mt: 1, color: 'primary.main', fontWeight: 600 }}>
                                Informações Adicionais
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                        </Grid>
                        <Grid item xs={12} md={12}>
                            <InputLabel required>Descrição do Objeto</InputLabel>
                            <OutlinedInput
                                fullWidth
                                multiline
                                rows={2}
                                name="objectDescription"
                                value={amendmentRequest.objectDescription || ''}
                                onChange={handleOnChange}
                                error={showValidationErrors && !amendmentRequest.objectDescription}
                            />
                            {showValidationErrors && !amendmentRequest.objectDescription && (
                                <FormHelperText error>Descrição do Objeto é obrigatória</FormHelperText>
                            )}
                        </Grid>

                        <Grid item xs={12} md={12}>
                            <InputLabel>Base Legal</InputLabel>
                            <OutlinedInput fullWidth multiline rows={2} name="legalBasis" value={amendmentRequest.legalBasis || ''} onChange={handleOnChange} />
                        </Grid>


                        <Grid item xs={12} md={12}>
                            <InputLabel>Finalidade Pública</InputLabel>
                            <OutlinedInput fullWidth multiline rows={2} name="publicPurpose" value={amendmentRequest.publicPurpose || ''} onChange={handleOnChange} />
                        </Grid>

                        <Grid item xs={12} md={12}>
                            <InputLabel>Justificativa Institucional</InputLabel>
                            <OutlinedInput fullWidth multiline rows={2} name="institutionalJustification" value={amendmentRequest.institutionalJustification || ''} onChange={handleOnChange} />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <InputLabel>Público-Alvo</InputLabel>
                            <OutlinedInput fullWidth autoComplete="off" name="targetAudience" value={amendmentRequest.targetAudience || ''} onChange={handleOnChange} />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <InputLabel>Local de Execução</InputLabel>
                            <OutlinedInput fullWidth autoComplete="off" name="executionLocation" value={amendmentRequest.executionLocation || ''} onChange={handleOnChange} />
                        </Grid>

                        {/* Anexos */}
                        {!isEdit && (
                            <>
                                <Grid item xs={12}>
                                    <Typography variant="h6" sx={{ mb: 2, mt: 1, color: 'primary.main', fontWeight: 600 }}>
                                        Anexos
                                    </Typography>
                                    <Divider sx={{ mb: 2 }} />
                                </Grid>

                                <Grid item xs={12}>
                                    <Button variant="outlined" component="label" startIcon={<AttachFileIcon />}>
                                        Selecionar Arquivos ({files.length})
                                        <input type="file" multiple hidden onChange={handleFilesChange} />
                                    </Button>
                                    {files.length > 0 && (
                                        <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                                            {files.map((f) => f.name).join(', ')}
                                        </Typography>
                                    )}
                                </Grid>
                            </>
                        )}

                        {/* Ações */}
                        <Grid item xs={12}>
                            <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2, mt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                                <Box sx={{ flex: '1 1 auto' }} />
                                <Button
                                    variant="outlined"
                                    startIcon={<CancelIcon />}
                                    onClick={handleCancel}
                                    sx={{ mr: 1 }}
                                >
                                    Cancelar
                                </Button>
                                <LoadingButton
                                    variant="contained"
                                    startIcon={<SaveIcon />}
                                    onClick={handleSave}
                                    loading={isSaving}
                                    disabled={isSaving}
                                >
                                    Salvar
                                </LoadingButton>
                            </Box>
                        </Grid>

                    </Grid>
                </Paper>
            </Grid>
        </React.Fragment>
    );
}
